/**
 * User Info Card Component
 *
 * Displays user profile information and tenant details in a card format.
 * Shows user name, tenant name, and last sign-in date.
 * In support mode, hides user info and only shows tenant details.
 *
 * @fileoverview User and tenant info display card
 *
 * @example
 * <UserInfoCard />
 */

import React from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  Badge,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Icon,
  Box,
} from '@chakra-ui/react';
import { MdBusiness, MdAccessTime } from 'react-icons/md';
import { useUser } from '../../Context/userContext';
import { useSupportMode } from '../../shared/support-mode';

/**
 * Format date to readable string
 *
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
function formatDate(dateString?: string): string {
  if (!dateString) {
    return 'Never';
  }

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Unknown';
  }
}

/**
 * Get user's display name
 *
 * @param user - User profile object
 * @returns Display name string
 */
function getUserDisplayName(user: {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email: string;
}): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.displayName) {
    return user.displayName;
  }
  return user.email.split('@')[0];
}

/**
 * Loading skeleton for the card
 */
const UserInfoCardSkeleton: React.FC = () => (
  <Card bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px">
    <CardHeader pb={2}>
      <Skeleton height="24px" width="120px" />
    </CardHeader>
    <CardBody pt={2}>
      <VStack align="stretch" spacing={4}>
        <HStack spacing={4}>
          <SkeletonCircle size="12" />
          <VStack align="start" spacing={1} flex={1}>
            <Skeleton height="20px" width="150px" />
            <Skeleton height="16px" width="200px" />
          </VStack>
        </HStack>
        <SkeletonText noOfLines={2} spacing={2} />
      </VStack>
    </CardBody>
  </Card>
);

/**
 * Info row component for consistent styling
 */
interface InfoRowProps {
  /** Icon to display */
  icon: React.ElementType;
  /** Label text */
  label: string;
  /** Value text */
  value: string;
  /** Optional badge */
  badge?: string;
  /** Badge color scheme */
  badgeColorScheme?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({
  icon,
  label,
  value,
  badge,
  badgeColorScheme = 'green',
}) => (
  <HStack spacing={3} align="start">
    <Icon as={icon} color="accent.primary" boxSize={5} mt={0.5} />
    <VStack align="start" spacing={0} flex={1}>
      <Text color="text.muted" fontSize="xs" textTransform="uppercase">
        {label}
      </Text>
      <HStack spacing={2}>
        <Text color="text.primary" fontWeight="medium">
          {value}
        </Text>
        {badge && (
          <Badge colorScheme={badgeColorScheme} size="sm">
            {badge}
          </Badge>
        )}
      </HStack>
    </VStack>
  </HStack>
);

/**
 * User Info Card Component
 *
 * Displays:
 * - User avatar with initials (hidden in support mode)
 * - User name and email (hidden in support mode)
 * - Tenant name with owner badge
 * - Last sign-in date (hidden in support mode)
 *
 * In support mode, only shows tenant/organization info since
 * the admin's personal info is not relevant when viewing another tenant.
 *
 * Shows loading skeleton while data is being fetched.
 *
 * @returns User info card component
 *
 * @example
 * // In dashboard layout
 * <VStack spacing={6}>
 *   <UserInfoCard />
 *   <PlaidTokenCard />
 * </VStack>
 */
export const UserInfoCard: React.FC = () => {
  const { user, selectedTenant, isLoading } = useUser();
  const { isInSupportMode } = useSupportMode();

  // Show skeleton while loading
  if (isLoading) {
    return <UserInfoCardSkeleton />;
  }

  // Don't render if no user
  if (!user) {
    return null;
  }

  const displayName = getUserDisplayName(user);
  const initials = user.firstName && user.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : displayName.substring(0, 2).toUpperCase();

  return (
    <Card
      bg="bg.surface"
      borderColor={isInSupportMode ? 'purple.500' : 'xacto.mutedNavy'}
      borderWidth="1px"
      shadow={isInSupportMode ? 'purple-glow' : 'cyan-glow'}
    >
      <CardHeader pb={2}>
        <Heading size="sm" color="text.primary">
          {isInSupportMode ? 'Tenant Information' : 'Account Information'}
        </Heading>
      </CardHeader>

      <CardBody pt={2}>
        <VStack align="stretch" spacing={5}>
          {/* User profile header - hidden in support mode */}
          {!isInSupportMode && (
            <>
              <HStack spacing={4}>
                <Avatar
                  size="lg"
                  name={displayName}
                  bg="xacto.electricCyan"
                  color="xacto.deepNavy"
                  fontWeight="bold"
                >
                  {initials}
                </Avatar>
                <VStack align="start" spacing={0} flex={1}>
                  <Text color="text.primary" fontWeight="semibold" fontSize="lg">
                    {displayName}
                  </Text>
                  <Text color="text.secondary" fontSize="sm">
                    {user.email}
                  </Text>
                  {user.globalRole === 'ADMIN' && (
                    <Badge colorScheme="purple" mt={1}>
                      Administrator
                    </Badge>
                  )}
                </VStack>
              </HStack>

              {/* Divider */}
              <Box borderBottomWidth="1px" borderColor="xacto.mutedNavy" />
            </>
          )}

          {/* Tenant info */}
          <InfoRow
            icon={MdBusiness}
            label="Organization"
            value={selectedTenant?.name ?? 'No organization'}
            badge={selectedTenant?.isOwner ? 'Owner' : undefined}
            badgeColorScheme="green"
          />

          {/* Last sign-in - hidden in support mode */}
          {!isInSupportMode && (
            <InfoRow
              icon={MdAccessTime}
              label="Last Sign-In"
              value={formatDate(user.lastLoginAt)}
            />
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default UserInfoCard;
